import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { ArrowLeft, BookOpen, Save, RotateCcw, Printer, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import chaptersData from '../data/chapters_expanded.json';
import PracticeScenario from './shared/PracticeScenario';
import PageMessaging from './shared/PageMessaging';

export default function ChapterViewer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chapterNum = parseInt(searchParams.get('chapter') || '1');
  const chapter = chaptersData.chapters.find(c => c.id === chapterNum);

  const [responses, setResponses] = useState(() => {
    const saved = localStorage.getItem(`chapter${chapterNum}Responses`);
    return saved ? JSON.parse(saved) : {};
  });

  const printPage = () => { window.print(); toast.success('Print dialog opened'); };
  
  const saveProgress = () => {
    localStorage.setItem(`chapter${chapterNum}Responses`, JSON.stringify(responses));
    toast.success('Progress saved!');
  };

  const reset = () => {
    if (window.confirm('Reset all responses for this chapter?')) {
      setResponses({});
      localStorage.removeItem(`chapter${chapterNum}Responses`);
      toast.success('Reset complete!');
    }
  };

  const updateResponse = (exerciseId, scenario, field, value) => {
    setResponses(prev => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        [scenario]: {
          ...(prev[exerciseId]?.[scenario] || {}),
          [field]: value
        }
      }
    }));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem(`chapter${chapterNum}Responses`, JSON.stringify(responses));
    }, 30000);
    return () => clearInterval(interval);
  }, [responses, chapterNum]);

  if (!chapter) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <p>Chapter not found</p>
      </div>
    );
  }

  const goToChapter = (num) => {
    if (num >= 1 && num <= 8) {
      navigate(`/workbook/chapter?chapter=${num}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Teacher's Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Chapter {chapter.id}: {chapter.title}</h1>
                <p className="text-slate-300 text-sm">Pages {chapter.pages}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => goToChapter(chapterNum - 1)} disabled={chapterNum === 1} variant="outline" size="sm" className="border-slate-600 text-white">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-slate-400 text-sm">Ch {chapterNum}/8</span>
              <Button onClick={() => goToChapter(chapterNum + 1)} disabled={chapterNum === 8} variant="outline" size="sm" className="border-slate-600 text-white">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <Button onClick={reset} variant="outline" size="sm" className="border-red-600 hover:bg-red-900/20 text-white">
                <RotateCcw className="h-4 w-4 mr-2" />Reset
              </Button>
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:bg-green-900/20 text-white">
                <Save className="h-4 w-4 mr-2" />Save
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-6xl mx-auto">
          {/* Chapter Description */}
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-6 mb-8">
            <p className="text-blue-100 leading-relaxed text-lg">{chapter.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {chapter.keyTopics?.map((topic, idx) => (
                <span key={idx} className="bg-blue-900/40 px-3 py-1 rounded-full text-blue-200 text-sm">{topic}</span>
              ))}
            </div>
          </div>

          {/* Sections */}
          {chapter.content?.sections?.map((section, idx) => (
            <div key={idx} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6 mb-6">
              <h3 className="text-2xl font-bold text-white mb-4">{section.title}</h3>
              {section.text && <p className="text-slate-200 leading-relaxed whitespace-pre-line mb-4">{section.text}</p>}
              {section.table && (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-slate-900/60">
                      <tr>
                        {section.table.headers.map((h, i) => (
                          <th key={i} className="px-4 py-3 text-slate-200 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.rows.map((row, i) => (
                        <tr key={i} className="border-t border-slate-700/30">
                          {row.map((cell, j) => (
                            <td key={j} className="px-4 py-3 text-slate-300">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}

          {/* Exercises */}
          {chapter.content?.exercises?.map((exercise, exIdx) => (
            <div key={exIdx} className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6 mb-8">
              <h3 className="text-2xl font-bold text-purple-100 mb-4">{exercise.title}</h3>
              <p className="text-purple-200 mb-4">{exercise.instructions}</p>
              
              {exercise.example && (
                <div className="bg-purple-900/30 rounded p-4 mb-6">
                  <p className="text-purple-200 font-semibold mb-2">Example: "{exercise.example.statement}"</p>
                  <p className="text-slate-200 text-sm mb-1"><span className="font-semibold">Typical:</span> {exercise.example.typical}</p>
                  {exercise.example.empathetic && <p className="text-green-200 text-sm mb-1"><span className="font-semibold">Empathetic:</span> {exercise.example.empathetic}</p>}
                  {exercise.example.honesty && <p className="text-blue-200 text-sm"><span className="font-semibold">Honesty:</span> {exercise.example.honesty}</p>}
                </div>
              )}

              {exercise.scenarios?.map((scenario, sIdx) => (
                <div key={sIdx} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-5 mb-4">
                  <h4 className="text-white font-semibold mb-3">{sIdx + 1}. "{scenario}"</h4>
                  <div className="space-y-3">
                    {exercise.type === 'response_practice' && (
                      <>
                        <Textarea 
                          value={responses[exercise.id]?.[scenario]?.typical || ''}
                          onChange={(e) => updateResponse(exercise.id, scenario, 'typical', e.target.value)}
                          placeholder="Typical response..."
                          className="bg-slate-900/40 border-slate-700 text-white placeholder:text-slate-500 min-h-[60px]"
                        />
                        <Textarea 
                          value={responses[exercise.id]?.[scenario]?.empathetic || ''}
                          onChange={(e) => updateResponse(exercise.id, scenario, 'empathetic', e.target.value)}
                          placeholder="Empathetic response..."
                          className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 min-h-[60px]"
                        />
                        <Textarea 
                          value={responses[exercise.id]?.[scenario]?.honesty || ''}
                          onChange={(e) => updateResponse(exercise.id, scenario, 'honesty', e.target.value)}
                          placeholder="Honesty response..."
                          className="bg-purple-900/20 border-purple-700/30 text-white placeholder:text-purple-300/40 min-h-[60px]"
                        />
                      </>
                    )}
                    {exercise.type === 'four_choice' && (
                      <>
                        <Textarea value={responses[exercise.id]?.[scenario]?.blameBack || ''} onChange={(e) => updateResponse(exercise.id, scenario, 'blameBack', e.target.value)} placeholder="Hear Blame, Blame Back" className="bg-red-900/20 border-red-700/30 text-white placeholder:text-red-300/40 min-h-[50px]" />
                        <Textarea value={responses[exercise.id]?.[scenario]?.blameSelf || ''} onChange={(e) => updateResponse(exercise.id, scenario, 'blameSelf', e.target.value)} placeholder="Hear Blame, Blame Self" className="bg-orange-900/20 border-orange-700/30 text-white placeholder:text-orange-300/40 min-h-[50px]" />
                        <Textarea value={responses[exercise.id]?.[scenario]?.myFeelingsNeeds || ''} onChange={(e) => updateResponse(exercise.id, scenario, 'myFeelingsNeeds', e.target.value)} placeholder="Hear My Own Feelings & Needs" className="bg-purple-900/20 border-purple-700/30 text-white placeholder:text-purple-300/40 min-h-[50px]" />
                        <Textarea value={responses[exercise.id]?.[scenario]?.speakerFeelingsNeeds || ''} onChange={(e) => updateResponse(exercise.id, scenario, 'speakerFeelingsNeeds', e.target.value)} placeholder="Hear Speaker's Feelings & Needs" className="bg-cyan-900/20 border-cyan-700/30 text-white placeholder:text-cyan-300/40 min-h-[50px]" />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Homework */}
          {chapter.content?.homework && (
            <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-6">
              <h3 className="text-2xl font-bold text-green-200 mb-4">Chapter {chapter.id} Homework</h3>
              <div className="space-y-3">
                {chapter.content.homework.items?.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Checkbox 
                      checked={responses.homework?.[idx] || false}
                      onCheckedChange={(checked) => setResponses(prev => ({
                        ...prev, 
                        homework: { ...(prev.homework || {}), [idx]: checked }
                      }))}
                    />
                    <p className="text-green-100">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-center text-slate-400 text-sm mt-8">💾 Auto-saves every 30 seconds</p>

          {/* Practice Scenario */}
          <PracticeScenario pageTitle={`Chapter ${chapter.id}: ${chapter.title}`} pageNumber={chapter.page} />

          {/* Page Messaging */}
          <PageMessaging pageTitle={`Chapter ${chapter.id}: ${chapter.title}`} pageNumber={chapter.page} />
        </div>
      </div>
    </div>
  );
}
